import { LoginCard } from "apps/common/authentication/common/LoginCard";
import { defaultSingleManagerPoweredByMessage } from "apps/common/authentication/common/LoginCard/constants";
import { FormPasswordInput } from "@jsq/ds/forms";
import { Form, Formik, type FormikHelpers } from "formik";
import React from "react";
import { useLoginMutation } from "common/types/mainHooks";
import { type LoginObject } from "common/types/mainTypes";
import { Flex } from "@jsq/ds/core";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import { RECOVERY_CODE_LENGTH, RECOVERY_CODE_ERROR_MESSAGE } from "./constants";
import { recoveryCodeEntryValidationSchema } from "./form-schemas";
import { type RecoveryCodeInput } from "./types";
import styles from "./styles.scss";

import { type UserContext } from "../../../common/constants";

const RECOVERY_CODE_INPUT_NAME = "recoveryCode";

export type RecoveryCardProps = {
  onRecoverySuccess: (loginObject: LoginObject) => void;
  onGoBackClick: () => void;
  onDoNotHaveRecoveryCodeClick: () => void;
  userContext: UserContext;
  email: string;
  password: string;
  arena?: UnauthenticatedArenaObjectType;
};

type RecoveryCodeFormik = {
  recoveryCode: string;
};

/**
 * A card to enter in recovery codes as part of account recovery flow
 */
export const RecoveryCard: React.FC<RecoveryCardProps> = ({
  onRecoverySuccess,
  onGoBackClick,
  onDoNotHaveRecoveryCodeClick,
  userContext,
  email,
  arena,
  password,
}: RecoveryCardProps) => {
  const buttonText = arena?.signInButtonTextColor || undefined;
  const buttonBackground = arena?.signInButtonBackgroundColor || undefined;

  const [login] = useLoginMutation();
  const handleFormSubmit = async (
    values: RecoveryCodeFormik,
    actions: FormikHelpers<RecoveryCodeFormik>,
  ) => {
    actions.setFieldError(RECOVERY_CODE_INPUT_NAME, "");
    try {
      const { data } = await login({
        variables: {
          loginInput: {
            twoFaRecoveryCode: values[RECOVERY_CODE_INPUT_NAME],
            userContext,
            arenaDomain: arena?.domain,
            email,
            password,
          },
        },
        context: { unauthenticated: true },
      });
      if (data?.login?.__typename === "LoginObject") {
        onRecoverySuccess(data.login as LoginObject);
      } else {
        throw new Error("Mutation Error");
      }
    } catch (e) {
      actions.setFieldError(RECOVERY_CODE_INPUT_NAME, RECOVERY_CODE_ERROR_MESSAGE);
      actions.setFieldValue(RECOVERY_CODE_INPUT_NAME, "");
    }
    /**
     * If onSubmit is async, then Formik will automatically set isSubmitting to false on your behalf once
     * it has resolved. This means you do NOT need to call formikBag.setSubmitting(false) manually. However,
     * if your onSubmit function is synchronous, then you need to call setSubmitting(false) on your own.
     * @see https://formik.org/docs/api/formik#onsubmit-values-values-formikbag-formikbag--void--promiseany
     */
    actions.setSubmitting(false);
  };

  return (
    <Formik<RecoveryCodeInput>
      initialValues={{ [RECOVERY_CODE_INPUT_NAME]: "" }}
      validationSchema={recoveryCodeEntryValidationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ handleSubmit, isValid, isSubmitting, values }) => (
        <LoginCard
          showPoweredByHeader={!!arena?.name}
          poweredByHelpMessage={defaultSingleManagerPoweredByMessage(arena?.name)}
          showCardTitle
          cardTitle="Enter a recovery code"
          titleDataTestId="recovery-card-header"
          description={
            <Flex alignContent="flex-start" flexDirection="column">
              Recovery codes were provided during activation.
              <br />
              {/* This `span` is required as we don't have a DS way for text with inline links :S */}
              <span
                className="underline"
                role="button"
                data-cy="do-not-have-recovery-code-button"
                tabIndex={0}
                onKeyPress={onDoNotHaveRecoveryCodeClick}
                onClick={onDoNotHaveRecoveryCodeClick}
              >
                Don&apos;t have a recovery code?
              </span>
            </Flex>
          }
          descriptionDataTestId="recovery-card-description"
          submitButtonText="Continue"
          submitButtonTestId="recovery-code-submit-button"
          onSubmitClick={handleSubmit}
          disableSubmitButton={
            !isValid ||
            isSubmitting ||
            values[RECOVERY_CODE_INPUT_NAME].length < RECOVERY_CODE_LENGTH
          }
          goBackButtonText="Nevermind, try two-factor authentication again"
          onGoBackClick={onGoBackClick}
          goBackButtonTestId="nevermind-use-2fa-button"
          submitButtonTextColor={buttonText}
          submitButtonBackgroundColor={buttonBackground}
        >
          <Form className={styles.RecoveryCard__form}>
            <FormPasswordInput
              name={RECOVERY_CODE_INPUT_NAME}
              label={`${RECOVERY_CODE_LENGTH}-digit recovery code`}
              isFullWidth
              placeholder=""
              readOnly={isSubmitting}
              maxLength={RECOVERY_CODE_LENGTH}
              data-testid="recoveryCodeInput"
              data-cy="recovery-code-input"
            />
          </Form>
        </LoginCard>
      )}
    </Formik>
  );
};

export default RecoveryCard;
